/**
 * The till's quantity +/- edits, sent one at a time with the newest value winning.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Each +/- click used to fire its own PATCH and apply whatever trade came back. Two quick clicks
 * (1→2→3) sent two requests that could land in either order: the reply for "2" arriving after
 * the reply for "3" put 2 on screen while the server held 3, and Complete could be pressed while
 * an edit was still on its way, so the receipt and the screen disagreed.
 *
 * Now every click updates the screen at once and records the line's target quantity. One request
 * is in flight at a time across the whole trade; when it returns, the next line whose target
 * differs from what the server has is sent. Clicks made meanwhile collapse into a single request
 * carrying the newest value. Only when nothing is left to send is the server's trade applied —
 * that answer reflects every edit, because they were sent in order. `pending` stays true until
 * then, which is what keeps Complete disabled.
 *
 * `reset()` (the trade was replaced) drops the queue and ignores any reply still on its way.
 */

export interface LineQuantityFailure {
  itemId: string
  error: unknown
}

export interface LineQuantityQueueOptions<R> {
  /** Send how far one line moved since the last answer; resolves with the server's answer. */
  send(itemId: string, quantityChange: number): Promise<R>
  /**
   * Everything queued has been sent. `last` is the newest server answer (null if the last send
   * failed); `failed` lists lines the server refused, whose on-screen value is now wrong.
   */
  onDrained(result: { last: R | null; failed: LineQuantityFailure[] }): void
  onPendingChange?(pending: boolean): void
}

export class LineQuantityQueue<R> {
  /** The quantity the user last asked for, per line. */
  private readonly targets = new Map<string, number>()
  /** The quantity the server last confirmed (or held before the first edit), per line. */
  private readonly confirmed = new Map<string, number>()
  private running = false
  private generation = 0

  constructor(private readonly options: LineQuantityQueueOptions<R>) {}

  /** True from the first queued edit until the server has answered the last one. */
  get pending(): boolean {
    return this.running
  }

  /** The newest quantity asked for on a line while edits are pending. */
  targetFor(itemId: string): number | undefined {
    return this.targets.get(itemId)
  }

  /**
   * Ask for `quantity` on a line. `current` is what the server holds for it right now; it is
   * only read the first time a line is touched in a burst of edits.
   */
  set(itemId: string, quantity: number, current: number): void {
    if (!this.confirmed.has(itemId)) this.confirmed.set(itemId, current)
    this.targets.set(itemId, quantity)
    void this.pump()
  }

  /** Forget everything queued; replies still in flight are ignored. */
  reset(): void {
    this.generation++
    this.targets.clear()
    this.confirmed.clear()
    if (this.running) {
      this.running = false
      this.options.onPendingChange?.(false)
    }
  }

  private nextToSend(): [string, number] | undefined {
    for (const [itemId, quantity] of this.targets) {
      if (this.confirmed.get(itemId) !== quantity) return [itemId, quantity]
    }
    return undefined
  }

  private async pump(): Promise<void> {
    if (this.running) return
    const generation = this.generation
    this.running = true
    this.options.onPendingChange?.(true)

    let last: R | null = null
    const failed: LineQuantityFailure[] = []

    for (let next = this.nextToSend(); next; next = this.nextToSend()) {
      const [itemId, quantity] = next
      try {
        const answer = await this.options.send(itemId, quantity - (this.confirmed.get(itemId) ?? quantity))
        if (generation !== this.generation) return
        last = answer
        this.confirmed.set(itemId, quantity)
      } catch (error) {
        if (generation !== this.generation) return
        // The server refused this value; stop chasing it. The caller re-reads the trade.
        last = null
        failed.push({ itemId, error })
        this.targets.delete(itemId)
        this.confirmed.delete(itemId)
      }
    }

    this.targets.clear()
    this.confirmed.clear()
    this.running = false
    this.options.onPendingChange?.(false)
    this.options.onDrained({ last, failed })
  }
}
